import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, XCircle, Clock, DollarSign, CreditCard } from 'lucide-react';
import { formatCurrency } from '@/lib/types/job';
import { PaymentProcessingModal } from '@/components/milestones/PaymentProcessingModal';
import { PaymentMethodSelectionModal } from '@/components/payments/PaymentMethodSelectionModal';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { calculateCcProcessingFee } from '@/lib/paymentUtils';
import type { POModificationRequest, PurchaseOrder } from '@/lib/types/job';

interface AdjustmentTransaction {
  id: string;
  adjustment_id: string | null;
  total_amount_cents: number;
  cc_processing_fee_cents: number | null;
  status: string;
}

interface AdjustmentHistoryProps {
  purchaseOrderId: string;
  purchaseOrder: PurchaseOrder;
  onAdjustmentApproved?: () => void;
  adjustmentTransactions?: AdjustmentTransaction[];
}

export function AdjustmentHistory({ purchaseOrderId, purchaseOrder, onAdjustmentApproved, adjustmentTransactions }: AdjustmentHistoryProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [adjustments, setAdjustments] = useState<POModificationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  
  // Payment methods and CC fee calculation (only for clients who can approve adjustments)
  const isClientUser = user?.role === 'client';
  const { paymentMethods } = useCustomerPaymentMethods(isClientUser ? purchaseOrder.organization_id : '');
  const { data: ccFeeSetting } = usePlatformSetting('cc_processing_fee_percentage');
  const ccFeePercentage = ccFeeSetting ? parseFloat(ccFeeSetting.setting_value) : 2.9;
  
  // Processing modal state
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentStatus, setAdjustmentStatus] = useState<'processing' | 'success' | 'error'>('success');
  const [adjustmentError, setAdjustmentError] = useState('');
  const [processingCorrelationId, setProcessingCorrelationId] = useState('');
  const [pendingAdjustmentId, setPendingAdjustmentId] = useState<string | undefined>();
  const [pendingAdjustment, setPendingAdjustment] = useState<POModificationRequest | undefined>();

  const fetchAdjustments = async () => {
    try {
      const { data, error } = await supabase
        .from('po_modification_requests')
        .select('*')
        .eq('purchase_order_id', purchaseOrderId)
        .neq('request_type', 'cancellation')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAdjustments((data || []) as POModificationRequest[]);
    } catch (error) {
      console.error('Error fetching adjustments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdjustments();

    // Subscribe to changes
    const channel = supabase
      .channel(`po_adjustments_${purchaseOrderId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'po_modification_requests',
          filter: `purchase_order_id=eq.${purchaseOrderId}`,
        },
        () => {
          fetchAdjustments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [purchaseOrderId, purchaseOrder.job_id]);

  const handleApprove = (adjustmentId: string, adjustment: POModificationRequest) => {
    // Credits (negative amounts) don't require payment
    if (adjustment.adjustment_amount_cents < 0) {
      handleApproveCredit(adjustmentId, adjustment);
      return;
    }
    
    // Debits require payment method selection
    setPendingAdjustmentId(adjustmentId);
    setPendingAdjustment(adjustment);
    setAdjustmentAmount(Math.abs(adjustment.adjustment_amount_cents));
    setShowPaymentMethodModal(true);
  };

  const handleApproveCredit = async (adjustmentId: string, adjustment: POModificationRequest) => {
    if (!user) return;

    const correlationId = crypto.randomUUID();
    setProcessingCorrelationId(correlationId);
    setProcessingId(adjustmentId);
    setAdjustmentAmount(Math.abs(adjustment.adjustment_amount_cents));
    setShowProcessingModal(true);
    setAdjustmentStatus('processing');
    setAdjustmentError('');

    try {
      console.log(`[AdjustmentHistory][${correlationId}] Approving credit adjustment:`, adjustmentId);

      const { data, error } = await invokeEdgeFunction('approve-credit-adjustment', {
        body: {
          adjustmentId,
          purchaseOrderId,
        },
        callerOperation: 'AdjustmentHistory.handleApproveCredit',
      });

      if (error) {
        console.error(`[AdjustmentHistory][${correlationId}] Error:`, error);
        throw error;
      }

      console.log(`[AdjustmentHistory][${correlationId}] Response:`, data);

      const result = data as { success?: boolean; error?: string } | undefined;

      if (result?.success) {
        setAdjustmentStatus('success');
        setAdjustmentError('');
        
        toast({
          title: 'Credit Approved',
          description: `The credit of ${formatCurrency(Math.abs(adjustment.adjustment_amount_cents))} has been applied to the purchase order.`,
        });

        setTimeout(() => {
          setShowProcessingModal(false);
          fetchAdjustments();
          onAdjustmentApproved?.();
        }, 2000);
      } else {
        throw new Error(result?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error(`[AdjustmentHistory][${correlationId}] Credit approval failed:`, error);
      setAdjustmentStatus('error');
      setAdjustmentError(error.message || 'Failed to approve credit');
      
      toast({
        title: 'Approval Failed',
        description: error.message || 'Failed to approve credit',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handlePaymentMethodConfirmed = async (selectedPaymentMethodId: string) => {
    if (!pendingAdjustmentId || !pendingAdjustment || !user) return;

    const adjustmentId = pendingAdjustmentId;
    const adjustment = pendingAdjustment;
    const baseAmount = Math.abs(adjustment.adjustment_amount_cents);

    // Determine if selected method is a card for fee calculation
    const selectedMethod = paymentMethods?.find(
      pm => pm.stripe_payment_method_id === selectedPaymentMethodId
    );
    const isCardPayment = selectedMethod?.payment_method_type === 'card';
    
    // Calculate total with CC fee if card payment
    const ccProcessingFeeCents = isCardPayment 
      ? calculateCcProcessingFee(baseAmount, ccFeePercentage) 
      : 0;
    const totalAmount = baseAmount + ccProcessingFeeCents;

    // Generate correlation ID for tracing
    const correlationId = crypto.randomUUID();
    setProcessingCorrelationId(correlationId);

    setShowPaymentMethodModal(false);
    setAdjustmentAmount(totalAmount); // Set total amount with CC fee for display
    setShowProcessingModal(true);
    setAdjustmentStatus('processing');
    setAdjustmentError('');
    setProcessingId(adjustmentId);

    try {
      console.log(`[AdjustmentHistory][${correlationId}] Approving adjustment with payment method:`, selectedPaymentMethodId);

      const { data, error } = await invokeEdgeFunction('approve-and-charge-adjustment', {
        body: {
          adjustmentId,
          purchaseOrderId,
          selectedPaymentMethodId,
        },
        callerOperation: 'AdjustmentHistory.handlePaymentMethodConfirmed',
      });

      if (error) {
        console.error(`[AdjustmentHistory][${correlationId}] Error:`, error);
        throw error;
      }

      console.log(`[AdjustmentHistory][${correlationId}] Response:`, data);

      const result = data as { 
        success?: boolean; 
        error?: string; 
        status?: string; 
        achPending?: boolean; 
        settlementEta?: string;
        isTraditionalAchPending?: boolean;
      } | undefined;

      // Handle Stripe ACH pending case
      if (result?.status === 'ach_pending' || result?.achPending) {
        setAdjustmentStatus('success');
        setAdjustmentError('');
        
        toast({
          title: 'ACH Payment Initiated',
          description: result?.settlementEta 
            ? `Payment will settle by ${new Date(result.settlementEta).toLocaleDateString()}. The adjustment will be applied after settlement.`
            : 'Payment is processing. The adjustment will be applied after settlement.',
        });

        setTimeout(() => {
          setShowProcessingModal(false);
          fetchAdjustments();
          onAdjustmentApproved?.();
        }, 3000);
        return;
      }

      // Handle Traditional ACH pending case
      if (result?.isTraditionalAchPending) {
        setAdjustmentStatus('success');
        setAdjustmentError('');
        
        toast({
          title: 'Traditional ACH Payment Initiated',
          description: 'Your payment will be applied once an administrator confirms receipt of funds.',
        });

        setTimeout(() => {
          setShowProcessingModal(false);
          fetchAdjustments();
          onAdjustmentApproved?.();
        }, 3000);
        return;
      }

      // Handle success case
      if (result?.success) {
        setAdjustmentStatus('success');
        setAdjustmentError('');
        
              toast({
                title: 'Change Order Approved',
                description: `Successfully charged ${formatCurrency(totalAmount)} and updated the purchase order.`,
              });

        setTimeout(() => {
          setShowProcessingModal(false);
          fetchAdjustments();
          onAdjustmentApproved?.();
        }, 2000);
      } else {
        throw new Error(result?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error(`[AdjustmentHistory][${correlationId}] Approval failed:`, error);
      setAdjustmentStatus('error');
      setAdjustmentError(error.message || 'Failed to process adjustment approval');
      
      toast({
        title: 'Approval Failed',
        description: error.message || 'Failed to process adjustment approval',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleRetryAdjustment = (adjustmentId: string, adjustment: POModificationRequest) => {
    handleApprove(adjustmentId, adjustment);
  };

  const handleDecline = async (adjustmentId: string) => {
    if (!user) return;
    
    setProcessingId(adjustmentId);
    
    try {
      // Fetch the adjustment details first
      const { data: adjustment, error: fetchError } = await supabase
        .from('po_modification_requests')
        .select('*')
        .eq('id', adjustmentId)
        .single();

      if (fetchError || !adjustment) throw fetchError || new Error('Adjustment not found');

      // Update the status
      const { error } = await supabase
        .from('po_modification_requests')
        .update({ 
          status: 'declined',
          responded_at: new Date().toISOString(),
          responded_by_user_id: user.id,
        })
        .eq('id', adjustmentId);

      if (error) throw error;

      toast({
        title: 'Change Order Declined',
        description: 'The change order request has been declined.',
      });

      // Send notification to both parties
      await invokeEdgeFunction('send-po-notification', {
        body: {
          type: 'adjustment_declined',
          purchaseOrderId,
          adjustmentData: {
            amount_cents: adjustment.adjustment_amount_cents,
            reason: adjustment.adjustment_reason,
            initiating_party: adjustment.initiating_party
          }
        },
        callerOperation: 'AdjustmentHistory.handleDecline',
      });

      fetchAdjustments();
    } catch (error) {
      console.error('Error declining adjustment:', error);
      toast({
        title: 'Error',
        description: 'Failed to decline adjustment',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  
  const canApprove = (adjustment: POModificationRequest) => {
    if (!user || adjustment.status !== 'pending') return false;
    
    // Don't allow approval if a payment is already in progress
    if (adjustment.payment_status === 'invoice_pending' || 
        adjustment.payment_status === 'pending_settlement' ||
        adjustment.payment_status === 'processing') {
      return false;
    }
    
    // Check if there's already a pending invoice transaction for this adjustment
    const hasInProgressTransaction = adjustmentTransactions?.some(
      tx => tx.adjustment_id === adjustment.id && 
           (tx.status === 'invoice_pending' || tx.status === 'held_in_escrow' || tx.status === 'pending_settlement')
    );
    if (hasInProgressTransaction) {
      return false;
    }
    
    // Client can approve vendor-initiated adjustments
    if (adjustment.initiating_party === 'vendor' && user.organization_id === purchaseOrder.organization_id) {
      return true;
    }
    
    // Vendor can approve client-initiated adjustments
    if (adjustment.initiating_party === 'client' && user.organization_id === purchaseOrder.vendor_organization_id) {
      return true;
    }
    
    return false;
  };

  const getStatusBadge = (status: string, paymentStatus?: string) => {
    // Special case: accepted but invoice not yet paid (payment_status='processing' for invoices)
    if (status === 'accepted' && paymentStatus === 'processing') {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Invoice Sent
        </Badge>
      );
    }

    const statusConfig = {
      pending: { label: 'Pending', variant: 'secondary' as const, icon: Clock },
      accepted: { label: 'Accepted', variant: 'default' as const, icon: CheckCircle },
      ach_pending_settlement: { label: 'ACH Pending', variant: 'secondary' as const, icon: Clock },
      declined: { label: 'Declined', variant: 'destructive' as const, icon: XCircle },
      payment_failed: { label: 'Payment Failed', variant: 'destructive' as const, icon: XCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Change Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading change orders...</p>
        </CardContent>
      </Card>
    );
  }

  if (adjustments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Change Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No change orders have been requested for this purchase order.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Change Orders</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {adjustments.map((adjustment, index) => (
            <div key={adjustment.id}>
              {index > 0 && <Separator className="my-4" />}
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {adjustment.adjustment_amount_cents > 0 ? 'Increase' : 'Credit'}:{' '}
                        {formatCurrency(Math.abs(adjustment.adjustment_amount_cents))}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{adjustment.adjustment_reason || 'No reason provided'}</p>
                  </div>
                  {getStatusBadge(adjustment.status, adjustment.payment_status)}
                </div>

                <div className="text-xs text-muted-foreground">
                  Requested by {adjustment.initiating_party === 'client' ? 'Client' : 'Vendor'} •{' '}
                  {new Date(adjustment.created_at).toLocaleString()}
                </div>

                {/* Payment breakdown for accepted/applied adjustments */}
                {adjustment.status === 'accepted' && (() => {
                  const matchingTx = adjustmentTransactions?.find(tx => tx.adjustment_id === adjustment.id);
                  const baseAmount = Math.abs(adjustment.adjustment_amount_cents);
                  const ccFeeCents = matchingTx?.cc_processing_fee_cents ?? 0;
                  
                  return (
                    <div className="bg-muted/50 rounded-md p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Change Order Amount</span>
                        <span>{formatCurrency(baseAmount)}</span>
                      </div>
                      {ccFeeCents > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <CreditCard className="h-3 w-3" />
                            Credit Card Processing Fee
                          </span>
                          <span className="text-orange-600">{formatCurrency(ccFeeCents)}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between text-sm font-medium">
                        <span>Total Charged</span>
                        <span>{formatCurrency(baseAmount + ccFeeCents)}</span>
                      </div>
                    </div>
                  );
                })()}

                {adjustment.status === 'declined' && adjustment.adjustment_reason?.includes('Payment failed') && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    Payment failed. {canApprove(adjustment) && 'Please retry the approval.'}
                  </div>
                )}

                {canApprove(adjustment) && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(adjustment.id, adjustment)}
                      disabled={processingId === adjustment.id}
                    >
                      {processingId === adjustment.id 
                        ? 'Processing...' 
                        : adjustment.adjustment_amount_cents < 0 
                          ? 'Approve' 
                          : 'Approve & Charge'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDecline(adjustment.id)}
                      disabled={processingId === adjustment.id}
                    >
                      Decline
                    </Button>
                  </div>
                )}

                {adjustment.status === 'declined' && adjustment.adjustment_reason?.includes('Payment failed') && canApprove(adjustment) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRetryAdjustment(adjustment.id, adjustment)}
                    disabled={processingId === adjustment.id}
                  >
                    Retry Payment
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Only render for client users who own this PO */}
      {user?.organization_id === purchaseOrder.organization_id && (
        <PaymentMethodSelectionModal
          open={showPaymentMethodModal}
          organizationId={purchaseOrder.organization_id}
          amount={adjustmentAmount}
          currentPaymentMethodId={undefined}
          jobPublicId={purchaseOrder.job?.public_id}
          onConfirm={handlePaymentMethodConfirmed}
          onCancel={() => setShowPaymentMethodModal(false)}
          loading={adjustmentStatus === 'processing'}
        />
      )}

      <PaymentProcessingModal
        open={showProcessingModal}
        amount={adjustmentAmount}
        status={adjustmentStatus}
        errorMessage={adjustmentError}
        transactionId={processingCorrelationId}
        onClose={() => setShowProcessingModal(false)}
      />
    </>
  );
}
